import React, { ReactNode } from 'react';
import classNames from 'classnames';

import TooltipPortal from './TooltipPortal';

type Placement = 'top' | 'right' | 'bottom' | 'left';

export interface TermTooltipProps {
  /** Unique on the page: becomes the DOM id `term-tooltip-{id}` the tooltip anchors to. */
  id: string;
  /** The term itself, shown as text with the dotted underline. Also the button's accessible name. */
  label: ReactNode;
  /** What the term means. Shown on hover and on keyboard focus, and announced through `aria-describedby`. */
  content: ReactNode;
  placement?: Placement;
  /** Wraps long explanations at this width (px). */
  limitWidth?: number;
  className?: string;
  dataCy?: string;
}

/**
 * A term that explains itself: the label reads as ordinary text with the dotted underline the app already uses for
 * "this term has an expansion" (`.has-caption`, the `<abbr>` convention, see `ColumnHeaderTooltip`), and its
 * explanation opens in a tooltip.
 *
 * Unlike the hover-only column header version, this one is a real `<button>`, so it is in the tab order: hover AND
 * keyboard focus open it, blur and Escape close it, and `aria-describedby` points a screen reader at the
 * explanation while it is open (all from `TooltipPortal`'s persistent mode). The button does nothing on click; it
 * exists to be focusable and to carry the description.
 */
const TermTooltip: React.FC<TermTooltipProps> = ({
  id,
  label,
  content,
  placement = 'top',
  limitWidth = 300,
  className,
  dataCy = 'term-tooltip',
}) => {
  const targetId = `term-tooltip-${id}`;

  return (
    <>
      <button
        type="button"
        id={targetId}
        className={classNames('term-tooltip has-caption', className)}
        data-cy={dataCy}
      >
        {label}
      </button>
      <TooltipPortal targetId={targetId} content={content} placement={placement} limitWidth={limitWidth} persistent />
    </>
  );
};

export default TermTooltip;
